<script setup lang="ts">
import { useFormValues } from 'vee-validate'
import { inject } from 'vue'
import { computed } from 'vue-demi'
import { useI18n } from 'vue-i18n'
import { ZoPPMPriloha } from '/@src/client-is'
import {
  useTovaryStore,
  useSluzbyStore,
  useOsobneVydavkyStore,
  useKapitaloveVydavkyStore,
  useOstatneVydavkyStore,
  useZjednoduseneVydavkyStore,
} from '/@src/stores/rozpocet'

const { t } = useI18n()
const formValues = useFormValues()
const isReadOnly = inject<boolean>('isReadOnly')
const tovaryStore = useTovaryStore()
const sluzbyStore = useSluzbyStore()
const osobneVydavkyStore = useOsobneVydavkyStore()
const kapitaloveVydavkyStore = useKapitaloveVydavkyStore()
const ostatneVydavkyStore = useOstatneVydavkyStore()
const zjednoduseneVydavkyStore = useZjednoduseneVydavkyStore()

const rozpocetStores = computed(() => ({
  tovary: tovaryStore,
  sluzby: sluzbyStore,
  osobneNaklady: osobneVydavkyStore,
  kapitaloveVydavky: kapitaloveVydavkyStore,
  ostatne: ostatneVydavkyStore,
  zjednoduseneVydavky: zjednoduseneVydavkyStore,
}))

const rozpocetPrilohy = computed(() =>
  Object.values(rozpocetStores.value)?.flatMap((store) =>
    Object.values(store.entries).flatMap(({ prilohy }) =>
      Object.values(prilohy).map((val) => val)
    )
  )
)

const podmienkyPoskytnutiaPrispevku = computed(
  () => formValues.value?.podmienkyPoskytnutiaPrispevku
)
const showPodmienkyPoskytnutiaPrispevku = computed(() =>
  formValues.value?.podmienkyPoskytnutiaPrispevku?.some(
    ({ prilohy }: { prilohy: ZoPPMPriloha[] }) =>
      prilohy?.length > 0 &&
      prilohy.some(
        (priloha) =>
          priloha?.priloha?.dokumenty && priloha?.priloha?.dokumenty?.length > 0
      )
  )
)

const zobrazitNadpis = computed(
  () =>
    podmienkyPoskytnutiaPrispevku?.value?.length > 0 || rozpocetPrilohy.value?.length > 0
)
</script>

<template>
  <template v-if="!isReadOnly">
    <template v-if="zobrazitNadpis">
      <IDSKHeading as="h2" variant="l">
        {{ t('zoppm.step13.attachment1.mainHeading') }}
      </IDSKHeading>
      <IDSKParagraph class="mb-0">
        {{ t('zoppm.step13.attachment1.mainInfo') }}
      </IDSKParagraph>
    </template>

    <Step13PodmienkyPodkytnutiaPrispevku
      v-if="showPodmienkyPoskytnutiaPrispevku"
      :podmienky-poskytnutia-prispevku="podmienkyPoskytnutiaPrispevku"
    />
    <Step13Rozpocet v-if="rozpocetPrilohy?.length > 0" :prilohy="rozpocetPrilohy" />

    <template v-if="zobrazitNadpis">
      <hr class="mb-9 mt-9" />
    </template>
  </template>

  <template v-else>
    <IDSKHeading as="h3" variant="l">{{
      t('zoppm.step13.attachment1.mainHeading')
    }}</IDSKHeading>
    <IDSKParagraph>{{ t('zoppm.step13.attachment1.mainInfo') }}</IDSKParagraph>

    <div v-if="showPodmienkyPoskytnutiaPrispevku">
      <IDSKHeading as="h4" variant="m">{{
        t('zoppm.step13.attachment1.part1.subHeading')
      }}</IDSKHeading>

      <UploadedFilesTable />
      <button class="idsk-button idsk-button--secondary">
        {{ t('zoppm.step13.editAttachment') }}
      </button>
    </div>

    <div v-if="rozpocetPrilohy?.length > 0">
      <IDSKHeading as="h4" variant="m">{{
        t('zoppm.step13.attachment1.part2.subHeading')
      }}</IDSKHeading>

      <UploadedFilesTable />
      <button class="idsk-button idsk-button--secondary">
        {{ t('zoppm.step13.editAttachment') }}
      </button>
    </div>

    <hr class="mb-9" />
  </template>
</template>

<style lang="scss"></style>
